package dev.lingvo.shared.screenshots.doc

import dev.lingvo.shared.StorePlatform
import kotlin.jvm.JvmInline
import kotlinx.serialization.Serializable

/** New documents use this version; supported older documents retain canonical bytes for their saved hashes. */
const val DESIGN_SCHEMA_VERSION = 3

/**
 * A project's screenshot design for one store platform (SPEC-B 1.1).
 *
 * One [Board] per device class: an iPhone 6.9" and an iPad 13" set have different aspects, so each gets its own
 * layout rather than one layout scaled to fit (the review's "one layout per device class"). The validator refuses a
 * document that names a device class twice.
 */
@Serializable
data class ScreenshotDesign(
    /** Always the first key written, so a reader can refuse an unknown version before decoding anything else. */
    val schemaVersion: Int,
    val platform: StorePlatform,
    /** Informational: which starter the design came from. Changing it does not re-apply the template. */
    val templateId: TemplateId? = null,
    val brand: BrandKit,
    /** Derived from the brand when a template is applied, then editable. Hex only: tokens resolve against it. */
    val palette: Palette,
    /** 1..8, at most one per device class. */
    val boards: List<Board>,
)

@Serializable
data class Board(
    val id: String,
    /** Track A's `DeviceClass.id`, e.g. "iphone_69". Checked, in its canonical spelling, through [DeviceClassRule]. */
    val deviceClass: String,
    /** Exact store pixels, e.g. 1290 x 2796. */
    val canvas: CanvasSize,
    /** 1..10 in plan order; at most 10 slots across all pages. */
    val pages: List<Page>,
    val mirrorRtl: Boolean = true,
    /** Null means the default rule (SPEC-B 4): sides 0.05 W, top and bottom 0.04 H. */
    val safeArea: Insets? = null,
    /** Null is an independently edited device override; linked boards refresh with their source. */
    val derivation: BoardDerivation? = null,
)

/** Kept inside the versioned document so a restore restores its derivation links atomically. */
@Serializable
data class BoardDerivation(val sourceDevice: String)

@Serializable
data class Page(
    val id: String,
    /** Size 1 is a single shot; 2..10 is a panorama strip, in order. */
    val slotIds: List<String>,
    val scene: SceneSpec,
    /** Bottom to top, in strip coordinates: x in [0, slotIds.size * W]. */
    val layers: List<Layer>,
    /** Keyed by BCP-47 locale. The canonical encoder writes these keys sorted. */
    val overrides: Map<String, LocaleOverride> = emptyMap(),
)

@Serializable
data class CanvasSize(val width: Int, val height: Int)

@Serializable
data class Insets(val top: Double, val right: Double, val bottom: Double, val left: Double)

/** A layer's frame in its parent's coordinates. Rotation is degrees clockwise about the centre (y down). */
@Serializable
data class Box(
    val x: Double,
    val y: Double,
    val w: Double,
    val h: Double,
    val rotation: Double = 0.0,
    val flipX: Boolean = false,
)

@Serializable
data class LayerBase(
    val id: String,
    val name: String = "",
    val box: Box,
    /** Scene binding (SPEC-B 6): which part of the arrangement this layer plays. */
    val role: Role? = null,
    /** The slot index inside a strip that the role belongs to. */
    val roleSlot: Int = 0,
    val opacity: Double = 1.0,
    val hidden: Boolean = false,
    val locked: Boolean = false,
    val blend: BlendMode = BlendMode.NORMAL,
    val shadow: Shadow? = null,
)

@Serializable
data class SceneSpec(
    val kind: SceneKind,
    val captionAt: CaptionAt = CaptionAt.TOP,
    val captionShare: Double = 0.22,
    val deviceScale: Double = 1.0,
    val deviceAnchor: Anchor = Anchor.CENTER,
    val tilt: Double = 8.0,
)

/** Per-locale geometry/type adjustments and schema-3 caption bindings. Translated words remain in strings. */
@Serializable
data class LocaleOverride(
    /** Null inherits the language/board choice. Schema 3 permits true to enable an RTL board's mirroring. */
    val mirror: Boolean? = null,
    /** Keyed by layer id; every id must exist on the page. */
    val patches: Map<String, LayerPatch> = emptyMap(),
    /** Schema 3: false retains an exact locale's independent legacy behavior after an explicit upgrade. */
    val inheritLanguage: Boolean = true,
)

@Serializable
data class LayerPatch(
    val box: Box? = null,
    val hidden: Boolean? = null,
    val fit: FitSpec? = null,
    val family: String? = null,
    val weight: Int? = null,
    val align: TextAlign? = null,
    val lineHeight: Double? = null,
    val letterSpacingEm: Double? = null,
    /** Schema 3: device-local caption key/slot binding; literal translated words are never allowed here. */
    val content: TextContent? = null,
)

@Serializable
data class BrandKit(
    val name: String,
    val primary: String? = null,
    val secondary: String? = null,
    val displayFamily: String? = null,
    val textFamily: String? = null,
    val logoAssetId: String? = null,
    /** At most 5 hex colours, from the icon (SPEC-B 7.3). */
    val iconPalette: List<String> = emptyList(),
    val voice: CaptionVoice = CaptionVoice(),
)

@Serializable
data class CaptionVoice(
    val tone: Tone = Tone.PLAIN,
    val notes: String = "",
    val avoid: List<String> = emptyList(),
)

/** Resolved colours, hex only. [ColorRef] tokens (`@bg`, `@accent/0.4`) resolve against these. */
@Serializable
data class Palette(
    val bg: String,
    val surface: String,
    val ink: String,
    val inkMuted: String,
    val accent: String,
    val accentInk: String,
    val slotAccents: List<String> = emptyList(),
)

@Serializable
@JvmInline
value class TemplateId(val id: String)

@Serializable
enum class Role { CAPTION, SUBCAPTION, EYEBROW, DEVICE_1, DEVICE_2, DEVICE_3, CARD_1, CARD_2, SCRIM, BAND, RIBBON, LOGO, DECOR }

@Serializable
enum class SceneKind { SINGLE, CAPTION_OVER_DEVICE, TILTED_PAIR, FAN_OF_THREE, FLOATING_CARDS, PANORAMA }

@Serializable
enum class FrameKind {
    PHONE_ISLAND, PHONE_NOTCH, PHONE_PUNCH_HOLE, PHONE_FLAT, TABLET_UNIFORM, TABLET_ANDROID,
    FOLDABLE_OPEN, WATCH_RECT, WATCH_ROUND, LAPTOP, BROWSER, TV, NONE,
}

@Serializable
enum class FrameFinish { AUTO, GRAPHITE, SILVER, MIDNIGHT, SAND, WHITE }

@Serializable
enum class ShapeKind { RECT, ELLIPSE, LINE, CURVE }

@Serializable
enum class TypeRole { DISPLAY, TEXT, SERIF, MONO }

@Serializable
enum class Tone { PLAIN, PLAYFUL, PREMIUM, TECHNICAL }

@Serializable
enum class TextAlign { START, CENTER, END }

@Serializable
enum class VAlign { TOP, MIDDLE, BOTTOM }

@Serializable
enum class TextTransform { NONE, UPPERCASE }

@Serializable
enum class HyphenationPolicy { NONE, SOFT_HYPHENS_ONLY }

@Serializable
enum class ImageFit { COVER, CONTAIN }

@Serializable
enum class CaptionAt { TOP, BOTTOM }

@Serializable
enum class Anchor { START, CENTER, END }

@Serializable
enum class BlendMode { NORMAL, MULTIPLY, SCREEN, OVERLAY, SOFT_LIGHT }
